import { Quote, Heart, Sparkles, Users } from "lucide-react";
import { Link } from "react-router-dom";

export default function About() {
  return (
    <div>
      <section className="container-wrap pt-16 md:pt-20 pb-12">
        <p className="overline mb-5"><span className="gold-divider align-middle mr-3" />About Us</p>
        <h1 className="font-serif-display text-5xl md:text-6xl text-brand-dark max-w-4xl leading-[1.0]">
          A foundation built on books, voices, and a New Orleans heart.
        </h1>
        <p className="mt-6 max-w-2xl text-brand-dark/75 text-lg leading-relaxed">
          WhoDat Cares Foundation is a nonprofit dedicated to improving reading literacy for school-age children ages 5 to 12 — sparking a lifelong love for reading, building purpose, identity, and strong communication skills in every young person we serve.
        </p>
      </section>

      {/* Founder Story */}
      <section className="container-wrap py-16">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <img
              src="https://images.pexels.com/photos/10638213/pexels-photo-10638213.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900"
              alt="Children reading"
              className="rounded-3xl shadow-xl shadow-brand-purple/15 w-full h-[520px] object-cover"
            />
          </div>
          <div className="md:col-span-7">
            <Quote className="w-10 h-10 text-brand-gold mb-4" />
            <p className="text-2xl md:text-3xl font-serif-display text-brand-dark leading-snug mb-6">
              "Too many of our youth are slipping through the cracks due to a lack of access to age-appropriate books, culturally relevant reading material, and positive literary role models."
            </p>
            <div className="space-y-5 text-brand-dark/80 leading-relaxed">
              <p>
                As the founder of WHO DAT Cares Foundation, I launched this initiative to directly address the systemic issue of low literacy rates among children in underserved communities.
              </p>
              <p>
                Our goal was to not only put books in their hands but to put <em className="text-brand-purple not-italic font-semibold">power</em> in their hands — the power to read, to imagine, and to write their own future.
              </p>
              <p>
                We saw an opportunity to disrupt the cycle by creating engaging, author-led reading sessions and mentorship programs that ignite a love for reading early. We took it a step further by teaching kids how to become published authors themselves — giving them agency, voice, and the confidence to tell their own stories.
              </p>
            </div>
            <p className="mt-6 text-sm uppercase tracking-[0.22em] font-semibold text-brand-purple">— Founder, WHO DAT Cares Foundation</p>
          </div>
        </div>
      </section>

      {/* Read With Me */}
      <section className="bg-brand-lavender/50 py-20">
        <div className="container-wrap grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <p className="overline mb-4"><span className="gold-divider align-middle mr-3" />Our Initiative</p>
            <h2 className="font-serif-display text-4xl md:text-5xl text-brand-dark mb-6 leading-tight">The Read With Me Book Club</h2>
            <p className="text-brand-dark/80 leading-relaxed mb-4">
              To further our mission, we established the Read With Me Book Club — a literacy program designed to provide reading books and engaging literacy experiences that help students grow as readers and thinkers.
            </p>
            <p className="text-brand-dark/80 leading-relaxed">
              To date, the Read With Me Book Club has distributed <strong className="text-brand-purple">over 1,000 reading books</strong> through community outreach events and partnerships in the New Orleans community.
            </p>
          </div>
          <div className="md:col-span-6 grid grid-cols-2 gap-4">
            {[
              { icon: Heart, t: "Community-First", d: "Built with and for New Orleans families." },
              { icon: Sparkles, t: "Culturally Relevant", d: "Books that reflect who our kids are." },
              { icon: Users, t: "Author-Led", d: "Real authors in real classrooms." },
              { icon: Quote, t: "Voice-Building", d: "Kids tell their own stories." },
            ].map(({ icon: Icon, t, d }, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 border border-brand-purple/10 shadow-sm">
                <Icon className="w-6 h-6 text-brand-gold mb-3" />
                <p className="font-serif-display text-lg text-brand-dark">{t}</p>
                <p className="text-sm text-brand-dark/70 mt-1">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Two Programs */}
      <section className="container-wrap py-20">
        <p className="overline mb-4"><span className="gold-divider align-middle mr-3" />Two Core Programs</p>
        <h2 className="font-serif-display text-4xl md:text-5xl text-brand-dark mb-12 max-w-3xl leading-tight">What we offer young readers and writers.</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="rounded-3xl border border-brand-purple/15 p-10 bg-white">
            <p className="text-brand-gold font-bold text-sm tracking-[0.2em] uppercase mb-3">Program 01</p>
            <h3 className="font-serif-display text-3xl text-brand-dark mb-4">Reading Literacy Program</h3>
            <p className="text-brand-dark/75 leading-relaxed">
              Teaches students foundational reading skills, comprehension, and vocabulary development through interactive sessions led by published children's authors.
            </p>
          </div>
          <div className="rounded-3xl border border-brand-gold/40 p-10 bg-brand-dark text-white">
            <p className="text-brand-gold font-bold text-sm tracking-[0.2em] uppercase mb-3">Program 02</p>
            <h3 className="font-serif-display text-3xl mb-4">Young Authors Program</h3>
            <p className="text-white/80 leading-relaxed">
              Guides youth step-by-step on how to structure, write, and illustrate books — empowering them to become published authors with their own books in our library.
            </p>
          </div>
        </div>
      </section>

      {/* Library */}
      <section className="container-wrap pb-24">
        <div className="rounded-3xl overflow-hidden grid md:grid-cols-12 bg-white shadow-md border border-brand-purple/10">
          <img
            src="https://images.pexels.com/photos/4969838/pexels-photo-4969838.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900"
            alt="WHO DAT BarberShop interior"
            className="md:col-span-6 h-72 md:h-full w-full object-cover"
          />
          <div className="md:col-span-6 p-10 md:p-12 flex flex-col justify-center">
            <p className="overline mb-3"><span className="gold-divider align-middle mr-3" />Our Library</p>
            <h2 className="font-serif-display text-3xl md:text-4xl text-brand-dark mb-4">Inside the WHO DAT BarberShop</h2>
            <p className="text-brand-dark/75 leading-relaxed mb-6">
              Our established library lives inside the WHO DAT BarberShop — a safe space where children can come at their leisure to pick up free books and connect with mentors. We're committed to ensuring every child has access to quality books and the chance to develop their imagination and talent.
            </p>
            <Link to="/donate" className="btn-primary self-start" data-testid="about-donate-cta">Support the Library</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
